import { type LocalServer } from "../data";

// Пустой массив по умолчанию — НЕ показываем mock-узлы пока API не ответит.
// Раньше использовался localServers из data.ts (9 фейковых узлов), из-за чего
// при первом рендере / reload мелькали несуществующие msk-01, ekb-01 и т.д.
const defaultLocals: LocalServer[] = [];

type MeshNodeLayout = { id: string; x: number; y: number };

const layout: MeshNodeLayout[] = [
  { id: "global", x: 50, y: 14 },
  { id: "spb-01", x: 50, y: 38 },
  { id: "msk-01", x: 18, y: 40 },
  { id: "msk-02", x: 32, y: 60 },
  { id: "ekb-01", x: 60, y: 60 },
  { id: "nsk-01", x: 88, y: 40 },
  { id: "kzn-01", x: 20, y: 80 },
  { id: "ros-01", x: 40, y: 86 },
  { id: "smr-01", x: 56, y: 86 },
  { id: "vld-01", x: 76, y: 80 },
];

const edges: [string, string][] = [
  ["global", "spb-01"],
  ["global", "msk-01"],
  ["global", "ekb-01"],
  ["global", "nsk-01"],
  // vld-01 всегда подключён напрямую к глобальному (fallback для удалённых узлов)
  ["global", "vld-01"],
  ["spb-01", "msk-02"],
  ["msk-01", "msk-02"],
  ["msk-02", "kzn-01"],
  ["kzn-01", "ros-01"],
  ["ros-01", "smr-01"],
  ["smr-01", "ekb-01"],
  ["ekb-01", "vld-01"],
  ["smr-01", "vld-01"],
];

const statusFill = { global: "#1f3a8a", online: "#16a34a", stale: "#d97706", offline: "#dc2626" };

export function MeshDiagram({
  onNodeClick,
  height = 320,
  locals = defaultLocals,
}: {
  onNodeClick?: (n: LocalServer) => void;
  height?: number;
  locals?: LocalServer[];
}) {
  const W = 700, H = height;
  const px = (n: number) => (n / 100) * W;
  const py = (n: number) => (n / 100) * H;
  const byId = Object.fromEntries(layout.map((n) => [n.id, n]));
  const localById = Object.fromEntries(locals.map((n) => [n.id, n]));
  const offlineIds = new Set(locals.filter((s) => s.status === "offline").map((s) => s.id));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="block w-full">
      <defs>
        <pattern id="meshgrid" width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.7" fill="#cbd5e1" opacity="0.55" />
        </pattern>
      </defs>
      <rect x="0" y="0" width={W} height={H} fill="url(#meshgrid)" rx="6" />

      {edges.map(([a, b]) => {
        // Рисуем ребро ТОЛЬКО когда оба конца существуют в реальных locals
        // (или это связь с глобалом, и локал есть). Без этого фильтра рисуем
        // фейковые пунктиры в никуда даже на пустой системе.
        const aExists = a === "global" || localById[a];
        const bExists = b === "global" || localById[b];
        if (!aExists || !bExists) return null;
        const A = byId[a], B = byId[b];
        const isDead = offlineIds.has(a) || offlineIds.has(b);
        return (
          <line
            key={`${a}-${b}`}
            x1={px(A.x)} y1={py(A.y)}
            x2={px(B.x)} y2={py(B.y)}
            // Используем цвета с достаточной яркостью для обеих тем
            stroke={isDead ? "#f87171" : "#60a5fa"}
            strokeOpacity={isDead ? 0.55 : 0.65}
            strokeWidth="1.5"
            strokeDasharray="5 4"
          />
        );
      })}

      {layout.map((n) => {
        if (n.id === "global") {
          const cx = px(n.x), cy = py(n.y);
          return (
            <g key="global">
              <circle cx={cx} cy={cy} r={14} fill={statusFill.global} />
              <circle cx={cx} cy={cy} r={20} fill="none" stroke={statusFill.global} strokeOpacity="0.18" strokeWidth="2" />
              <text x={cx} y={cy + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700" fontFamily="IBM Plex Sans">G</text>
              <text x={cx} y={cy + 30} textAnchor="middle" fontSize="10" fill="#94a3b8" fontFamily="IBM Plex Mono">GLOBAL</text>
            </g>
          );
        }
        const node = localById[n.id];
        if (!node) return null;
        const cx = px(n.x), cy = py(n.y);
        const stroke = statusFill[node.status];
        return (
          <g
            key={n.id}
            className={onNodeClick ? "cursor-pointer transition-transform hover:scale-110" : ""}
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
            onClick={() => onNodeClick?.(node)}
          >
            <circle cx={cx} cy={cy} r={14} fill="transparent" />
            <circle
              cx={cx} cy={cy} r={9}
              fill="#fff" stroke={stroke} strokeWidth={2.5}
              className={node.status === "offline" ? "node-pulse" : ""}
            />
            <text x={cx} y={cy + 22} textAnchor="middle" fontSize="10" fill="#94a3b8" fontFamily="IBM Plex Mono">
              {n.id}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
